import { AbstractController } from '@/Emulator/Controller/AbstractController';
import { GamepadController }  from '@/Emulator/Controller/GamepadController';
import { KeyboardController } from '@/Emulator/Controller/KeyboardController';
import { Joypad }             from '@/Emulator/IO/Joypad';
import { EventDispatcher }    from '@/Event';

/**
 * Routes input signals from physical controllers to designated Joypad
 * instances for the emulator to use.
 */
export class InputRouter extends EventDispatcher<IControllerRouterEvents>
{
    public readonly controllers: AbstractController[] = [];

    private p1Controller: Nullable<AbstractController> = null;
    private p2Controller: Nullable<AbstractController> = null;

    private p1subscriptions: IDisposable[] = [];
    private p2subscriptions: IDisposable[] = [];

    constructor(
        private readonly joypad1: Joypad,
        private readonly joypad2: Joypad,
    )
    {
        super();

        // Always start with a keyboard controller.
        this.controllers.push(new KeyboardController());

        this.setPlayer1Controller(this.controllers[0]);

        window.addEventListener('gamepadconnected', this.onGamepadConnected.bind(this));
        window.addEventListener('gamepaddisconnected', this.onGamepadDisconnected.bind(this));

        setTimeout(() => {
            this.dispatch('input-devices-changed');
        });
    }

    public tick(): void
    {
        for (const controller of this.controllers) {
            controller.tick();
        }
    }

    public setPlayer1Controller(controller: Nullable<AbstractController>): void
    {
        this.p1subscriptions.forEach((subscription: IDisposable): void => subscription.dispose());
        this.p1Controller = controller;

        if (controller) {
            this.p1subscriptions = this.bindController(controller, this.joypad1);
        }
    }

    public setPlayer2Controller(controller: Nullable<AbstractController>): void
    {
        this.p2subscriptions.forEach((subscription: IDisposable): void => subscription.dispose());
        this.p2Controller = controller;

        if (controller) {
            this.p2subscriptions = this.bindController(controller, this.joypad2);
        }
    }

    public isPlayer1Controller(controller: AbstractController): boolean
    {
        return this.p1Controller?.name === controller.name;
    }

    public isPlayer2Controller(controller: AbstractController): boolean
    {
        return this.p2Controller?.name === controller.name;
    }

    /**
     * Binds the given controller to the given joypad and returns a new event
     * subscription list that can later be disposed of.
     */
    private bindController(controller: AbstractController, joypad: Joypad): IDisposable[]
    {
        const subscriptions: IDisposable[] = [
            controller.on('button-start', (state: boolean): void => joypad.setButtonState(Joypad.BUTTON_START, state)),
            controller.on('button-select', (state: boolean): void => joypad.setButtonState(Joypad.BUTTON_SELECT, state)),
            controller.on('button-up', (state: boolean): void => joypad.setButtonState(Joypad.BUTTON_UP, state)),
            controller.on('button-down', (state: boolean): void => joypad.setButtonState(Joypad.BUTTON_DOWN, state)),
            controller.on('button-left', (state: boolean): void => joypad.setButtonState(Joypad.BUTTON_LEFT, state)),
            controller.on('button-right', (state: boolean): void => joypad.setButtonState(Joypad.BUTTON_RIGHT, state)),
            controller.on('button-a', (state: boolean): void => joypad.setButtonState(Joypad.BUTTON_A, state)),
            controller.on('button-b', (state: boolean): void => joypad.setButtonState(Joypad.BUTTON_B, state)),
            controller.on('save-state', (state: boolean): void => {
                if (state) {
                    this.dispatch('request-save-state');
                }
            }),
            controller.on('load-state', (state: boolean): void => {
                if (state) {
                    this.dispatch('request-load-state');
                }
            }),
            controller.on('disconnected', () => {
                this.disconnect(subscriptions);
                const index = this.controllers.findIndex(c => c.name === controller.name);
                this.controllers.splice(index, 1);
                this.dispatch('input-devices-changed');
            }),
        ];

        return subscriptions;
    }

    private disconnect(subscriptions: IDisposable[]): void
    {
        subscriptions.forEach(sub => sub.dispose());
    }

    private onGamepadConnected(e: GamepadEvent): void
    {
        this.controllers.push(new GamepadController(`(${e.gamepad.index}) ${e.gamepad.id}`, e.gamepad.index));
        this.dispatch('input-devices-changed');
    }

    private onGamepadDisconnected(e: GamepadEvent): void
    {
        const index: number                  = this.controllers.findIndex(gp => gp.name === `(${e.gamepad.index}) ${e.gamepad.id}`);
        const controller: AbstractController = this.controllers[index];

        controller?.dispose();
    }
}

interface IControllerRouterEvents
{
    'input-devices-changed': [];
    'request-save-state': [];
    'request-load-state': [];
}
